'use client';

import { motion } from 'framer-motion';
import {
  Calculator,
  Users,
  DollarSign,
  TrendingUp,
  BarChart3,
  Sparkles,
  ArrowRight,
  BookOpen,
} from 'lucide-react';

import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

export function ToolsDirectory() {
  const { t } = useTranslation();

  const TOOLS = [
    {
      id: 'ai-adoption-guide',
      title: t.tools['AI Adoption Guide'],
      description:
        t.tools['A comprehensive, research-backed guide to implementing AI in customer support. 6 chapters of actionable insights.'],
      icon: BookOpen,
      href: '/tools/ai-adoption-guide',
      color: 'bg-black text-white',
      iconBg: 'bg-black',
      popular: true,
      stats: t.tools['28 min read • 15+ sources'],
    },
    {
      id: 'roi-calculator',
      title: t.tools['ROI Calculator'],
      description:
        t.tools['Calculate how much you could save by automating customer support with AI. See your potential yearly savings.'],
      icon: Calculator,
      href: '/tools/roi-calculator',
      color: 'bg-green-100 text-green-700',
      iconBg: 'bg-green-500',
      popular: true,
      stats: t.tools['77% avg automation rate'],
    },
    {
      id: 'team-size-calculator',
      title: t.tools['Team Size Calculator'],
      description:
        t.tools['Figure out exactly how many support agents you need based on ticket volume, channels, and SLA requirements.'],
      icon: Users,
      href: '/tools/team-size-calculator',
      color: 'bg-blue-100 text-blue-700',
      iconBg: 'bg-blue-500',
      popular: true,
      stats: t.tools['Research-backed formulas'],
    },
    {
      id: 'support-budget',
      title: t.tools['Support Budget Planner'],
      description:
        t.tools['Plan your annual support budget based on industry benchmarks, team location, and company size.'],
      icon: DollarSign,
      href: '/tools/support-budget',
      color: 'bg-emerald-100 text-emerald-700',
      iconBg: 'bg-emerald-500',
      popular: false,
      stats: `${new Date().getFullYear()} ${t.tools['salary & cost data']}`,
    },
    {
      id: 'benchmark',
      title: t.tools['Support Benchmarking'],
      description:
        t.tools['Compare your cost per ticket, response time, and CSAT against industry peers. See where you rank.'],
      icon: BarChart3,
      href: '/tools/benchmark',
      color: 'bg-purple-100 text-purple-700',
      iconBg: 'bg-purple-500',
      popular: false,
      stats: t.tools['8 industries covered'],
    },
    {
      id: 'ai-readiness',
      title: t.tools['AI Readiness Assessment'],
      description:
        t.tools['Get a personalized score on how ready your support team is for AI automation. Includes actionable recommendations.'],
      icon: Sparkles,
      href: '/tools/ai-readiness',
      color: 'bg-orange-100 text-orange-700',
      iconBg: 'bg-orange-500',
      popular: false,
      stats: t.tools['6 factors analyzed'],
    },
  ];

  return (
    <section className="min-h-screen bg-white pt-32 pb-20 md:pt-40">
      <div className="container max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-16 text-center"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-medium text-white">
            <Calculator className="size-4" />
            {t.tools['Free Tools']}
          </div>
          <h1 className="mb-4 font-serif text-4xl md:text-5xl lg:text-6xl">
            {t.tools['Support Operations Tools']}
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            {t.tools['Free calculators and assessments to help you optimize your customer support. All tools are backed by industry research and provide actionable insights.']}
          </p>
        </motion.div>

        {/* Tools Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {TOOLS.map((tool, index) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.05 }}
            >
              <Link
                href={tool.href}
                className="group relative flex h-full flex-col rounded-2xl border-2 border-neutral-200 bg-white p-6 transition-all hover:border-black hover:shadow-lg"
              >
                {tool.popular && (
                  <div className="absolute -top-3 right-4 rounded-full bg-black px-3 py-1 text-xs font-medium text-white">
                    {t.tools['Popular']}
                  </div>
                )}

                <div className="mb-4 flex items-start justify-between">
                  <div
                    className={`flex size-12 items-center justify-center rounded-xl ${tool.iconBg} text-white`}
                  >
                    <tool.icon className="size-6" />
                  </div>
                  <ArrowRight className="size-5 text-neutral-300 transition-all group-hover:translate-x-1 group-hover:text-black" />
                </div>

                <h3 className="mb-2 text-xl">{tool.title}</h3>
                <p className="mb-4 flex-1 text-sm text-neutral-600">
                  {tool.description}
                </p>

                <div
                  className={`inline-flex w-fit items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${tool.color}`}
                >
                  <TrendingUp className="size-3" />
                  {tool.stats}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
